import { useState } from "react";
import { useCatalog } from "../lib/products";
import { useOpenFromUrl } from "../lib/openFromUrl";
import { ArrowRight, Check } from "lucide-react";
import { OfficinaSheet } from "./OfficinaSheet";
import { OFFICINA_CATEGORIES } from "../data/officinaCategories";
import { Reveal } from "./Reveal";
import { VideoGallery } from "./VideoGallery";
import { RAVAGLIOLI_VIDEOS } from "../data/christVideos";
import { scrollToSection } from "../lib/scroll";



const ADVANTAGES = [
    "Progettazione layout officina e centro revisione",
    "Installazione, collaudo e formazione del personale",
    "Assistenza tecnica e ricambi originali per tutte le attrezzature fornite",
    "Manutenzione programmata e verifiche periodiche di legge",
];

const CategoryCard = ({ c, i, onOpen }) => (
    <Reveal delay={0.05 + i * 0.06} className={c.wide ? "sm:col-span-2" : ""}>
        <article
            data-testid={`officina-category-${i}`}
            onClick={() => onOpen(c)}
            className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.05] transition-[background-color,transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-[#E30613]/40 hover:bg-white/[0.09]"
        >
            <div
                className={`relative flex items-center justify-center overflow-hidden ${
                    c.photo ? "h-44" : "h-44 bg-gradient-to-b from-white/[0.06] to-transparent p-5"
                }`}
            >
                <img
                    src={c.img}
                    alt={c.title}
                    loading="lazy"
                    className={`transition-transform duration-700 group-hover:scale-105 ${
                        c.photo ? "h-full w-full object-cover" : "max-h-full w-auto object-contain drop-shadow-[0_16px_24px_rgba(0,0,0,0.45)]"
                    }`}
                />
                {c.brand && (
                    <span className="absolute left-3 top-3 rounded-full bg-[#0B1F3A]/80 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.14em] text-white/80 backdrop-blur">
                        {c.brand}
                    </span>
                )}
            </div>
            <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-lg font-bold text-white">{c.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-white/65">{c.desc}</p>
                <div className="mt-4 flex items-center gap-2 text-xs font-bold text-white">
                    Apri scheda
                    <ArrowRight className="h-3.5 w-3.5 text-[#E30613] transition-transform duration-300 group-hover:translate-x-1" />
                </div>
            </div>
        </article>
    </Reveal>
);

const customOfficina = (d) => ({
    key: d.key,
    title: d.name,
    desc: d.short,
    img: d.img || "/officina/ponte.png",
    brand: d.sub,
    photo: !!d.img,
    description: d.description,
    custom: true,
});

export const OfficinaSection = () => {
    const [open, setOpen] = useState(null);
    const categories = useCatalog("officina", OFFICINA_CATEGORIES, "desc", customOfficina);
    useOpenFromUrl(categories, setOpen);
    return (
    <>
    <section
        id="autoriparatore"
        data-testid="autoriparatore-section"
        className="relative overflow-hidden bg-[#0B1F3A] py-20 text-white lg:py-28"
    >
        <img
            src="/officina/officina-ravaglioli.jpg"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.16]"
        />
        <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0B1F3A] via-[#0B1F3A]/85 to-[#0B1F3A]"
        />
        <div
            aria-hidden="true"
            className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-[#E30613]/15 blur-3xl"
        />

        <div className="relative mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
            <div className="grid gap-12 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <Reveal>
                        <span
                            data-testid="officina-eyebrow"
                            className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 py-1.5 pl-2 pr-4 backdrop-blur"
                        >
                            <span className="rounded-full bg-white px-3 py-1.5">
                                <img
                                    src="/brands/ravaglioli-logo-dark.png"
                                    alt="Ravaglioli"
                                    className="h-5 w-auto"
                                />
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/80">
                                Partner Ravaglioli
                            </span>
                        </span>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <span className="mt-8 block text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]">
                            Settore 02 · Autoriparatore
                        </span>
                        <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]">
                            L'officina completa,{" "}
                            <span className="text-[#E30613]">dal ponte all'utensile.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <p className="mt-6 text-base leading-relaxed text-white/75 sm:text-lg">
                            Attrezziamo officine meccaniche, carrozzerie, gommisti, centri
                            revisione e reparti post-vendita delle concessionarie: sollevamento,
                            servizio gomme, assetto, diagnosi, forni di verniciatura, piani
                            aspiranti e banchi di tiro, utensileria, aspirazione gas e gestione
                            olio. Un unico fornitore tecnico, con assistenza diretta in tutto il
                            Sud Italia.
                        </p>
                    </Reveal>
                    <Reveal delay={0.26}>
                        <ul className="mt-8 space-y-3.5" data-testid="officina-advantages">
                            {ADVANTAGES.map((a) => (
                                <li key={a} className="flex items-start gap-3">
                                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#E30613]/20">
                                        <Check className="h-3.5 w-3.5 text-[#E30613]" />
                                    </span>
                                    <span className="text-sm font-medium text-white/90 sm:text-base">
                                        {a}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.34}>
                        <button
                            data-testid="officina-cta-button"
                            onClick={() => scrollToSection("contatti")}
                            className="group mt-10 inline-flex items-center gap-2.5 rounded-full bg-[#E30613] px-7 py-3.5 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:bg-[#C20410] active:scale-95"
                        >
                            Progetta la tua officina con noi
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </Reveal>
                </div>

                <div className="lg:col-span-7">
                    <div className="grid gap-5 sm:grid-cols-2" data-testid="officina-categories-grid">
                        {categories.map((c, i) => (
                            <CategoryCard key={c.key} c={c} i={i} onOpen={setOpen} />
                        ))}
                    </div>
                </div>
            </div>

            <VideoGallery
                testid="ravaglioli-video-block"
                eyebrow="L'officina in azione"
                title="Smontagomme, ponti e assetto ruote Ravaglioli."
                desc="Video dal canale YouTube ufficiale Ravaglioli: smontagomme, colonne mobili, sollevatori, assetto ruote e linee revisione."
                brand="Ravaglioli"
                videos={RAVAGLIOLI_VIDEOS}
                dark
            />
        </div>
    </section>
    <OfficinaSheet c={open} onClose={() => setOpen(null)} />
    </>
    );
};
